import React, { useContext, useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import {
  TextField,
  InputAdornment,
  Grid,
  Divider,
  Button,
  MenuItem,
  Select,
  FormHelperText,
} from '@material-ui/core'
import { ReactComponent as AddSvg } from '../../assets/add.svg'
import {
  InsurancePurchaseContextStore,
  INSURANCE_CONTEXT_ACTIONS,
} from '../../context/InsuracePurchaseContext'
import { PolicyManagementContextStore } from '../../context/PolicyManagementContext'
import FlexView from 'react-flexview/lib'
import NewFillButton from '../Core/NewFillButton'
import {
  EventEmitterService,
  EventKey,
} from '../../services/EventEmitterService'
import { Action } from '../../models/Action'
import { OptionalTag } from '../Core/OptionalTag'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import { BrowserView } from 'react-device-detect'
import policymanagementApi from '../../api/policymanagement.api'
import { awaitWithLoading } from '../../services/utils'
import {
  FirebaseService,
  FirebaseConfigKey,
} from '../../services/FirebaseService'
import { COUNTRIES, STATE_EXPIRY_TTL, EMAIL_REGEX } from '../../Constants'
import LocalizationService from '../../services/LocalizationService'
import StorageService from '../../services/StorageService'
import { UserApi } from '../../api/user.api'
import { AdditionalInsuredList } from './AdditionalInsuredList'
import { SessionService } from '../../services/SessionService'

const findMaxNewId = (aiList) => {
  let maxId = -1
  for (let ai of aiList) {
    if (ai.newId != undefined && ai.newId > maxId) maxId = ai.newId
  }
  return maxId + 1
}

export const AdditionalInsuredPureForm = ({
  additionalInsuredList,
  allAdditionalInsuredList,
  onAdditionalInsuredListUpdate,
  onFinish,
  ...props
}) => {
  const { register, trigger, watch, setValue, errors, control, getValues } =
    useForm({ mode: 'onChange' })
  const [allAi, setAllAi] = useState(allAdditionalInsuredList)
  const [activeAi, setActiveAi] = useState(additionalInsuredList)
  const [showForm, setShowForm] = useState(allAdditionalInsuredList.length == 0)
  const [editedAi, setEditedAi] = useState(undefined)
  const [stateEditable, setStateEditable] = useState(true)
  const [states, setStates] = useState([])
  const stateWatch = watch('state', '')
  const [newAiId, setNewAiId] = useState(findMaxNewId(allAi))
  const onCheckBoxChange = (event) => {
    let handledAi = allAi.find((q) => q.id == event.target.name)
    if (!handledAi) {
      handledAi = allAi.find((q) => q.newId == event.target.name)
    }
    if (handledAi) {
      let newAiList = event.target.checked
        ? [...activeAi, handledAi]
        : activeAi.filter(
            (ai) =>
              (handledAi.id != undefined && ai.id != handledAi.id) ||
              (handledAi.newId != undefined && handledAi.newId != ai.newId)
          )
      setActiveAi(newAiList)
    }
  }

  const onSubmit = async () => {
    if (showForm) {
      let valid

      // Admin can do whatever he wants
      if (SessionService.isAdmin()) {
        valid = true
      } else valid = await trigger()

      if (valid) {
        let aiData = getValues()

        if (SessionService.isAdmin() && aiData.state == 'statedefault') {
          aiData.state = ''
        }

        if (editedAi != undefined) {
          aiData.id = editedAi.id
          aiData.newId = editedAi.newId
          let newActiveAiList = activeAi.map((q) =>
            (aiData.newId != undefined && aiData.newId == q.newId) ||
            (aiData.id != undefined && aiData.id == q.id)
              ? aiData
              : q
          )
          let newAllAiList = allAi.map((q) =>
            (aiData.newId != undefined && aiData.newId == q.newId) ||
            (aiData.id != undefined && aiData.id == q.id)
              ? aiData
              : q
          )
          setActiveAi(newActiveAiList)
          setAllAi(newAllAiList)
        } else {
          aiData.newId = newAiId
          setActiveAi([...activeAi, aiData])
          setAllAi([...allAi, aiData])
          setNewAiId(newAiId + 1)
        }
      } else {
        return
      }
    } else {
      onAdditionalInsuredListUpdate(activeAi, allAi)
      onFinish()
    }
    setShowForm(false)
    setEditedAi(undefined)
  }

  const onEdit = (ai) => {
    setEditedAi(ai)
    setShowForm(true)
  }

  const addAnother = () => setShowForm(true)
  const isEditMode = () => editedAi != undefined
  const shrink = () => (isEditMode() ? true : undefined)

  const setStateFromAutocomplete = (e) => {
    let selected = states.filter(
      (i) => i.name.toLowerCase() == e.target.value.toLowerCase()
    )
    if (selected.length > 0) setValue('state', selected[0].code)
  }

  useEffect(() => {
    if (editedAi != undefined && showForm) {
      setValue('name', editedAi.name)
      setValue('email', editedAi.email)
      setValue('state', editedAi.state)
      setValue('city', editedAi.city)
      setValue('address', editedAi.address)
      setValue('zip', editedAi.zip)
    } else if (
      showForm &&
      editedAi == undefined &&
      FirebaseService.getValue(FirebaseConfigKey.COUNTRY) == COUNTRIES.UK
    ) {
      setValue('state', 'United Kingdom')
      setStateEditable(false)
    }
  }, [editedAi, showForm])

  useEffect(() => {
    const fetchData = async () => {
      let res = await UserApi.getStates()

      if (res.ok) {
        setStates(res.parsedData)
        StorageService.setWithExpiry(
          'STATE_LIST',
          res.parsedData,
          STATE_EXPIRY_TTL
        )
      } else {
        EventEmitterService.dispatch(EventKey.ShowError, res)
      }
    }

    if (StorageService.getWithExpiry('STATE_LIST')) {
      setStates(StorageService.getWithExpiry('STATE_LIST'))
    } else {
      fetchData()
    }
  }, [])

  const zipcodeRegex = new RegExp(
    FirebaseService.getValue(FirebaseConfigKey.ZIPCODE_REGEX)
  )

  return (
    <React.Fragment>
      <BrowserView>
        <div className="text-body blue textcenter bold margin-bottom-basic">
          Add Additional Insured
        </div>
      </BrowserView>
      {!showForm && (
        <AdditionalInsuredList
          onCheckBoxChange={onCheckBoxChange}
          onEdit={onEdit}
          allAdditionalInsured={allAi}
          activeAdditionalInsured={activeAi}
        />
      )}
      {showForm && (
        <React.Fragment>
          <FlexView column>
            <TextField
              fullWidth
              variant="outlined"
              label="Name"
              className="margin-top-small"
              name="name"
              inputRef={register({ required: true })}
              helperText={errors.name && errors.name.type && 'Name is required'}
              error={!!errors.name}
              InputLabelProps={{ shrink: shrink() }}
            />
            <TextField
              fullWidth
              variant="outlined"
              className="margin-top-basic"
              label="Email"
              type="email"
              name="email"
              inputRef={register({
                pattern: {
                  value: EMAIL_REGEX,
                  message: 'Invalid email address',
                },
              })}
              InputProps={{
                endAdornment: (
                  <InputAdornment>
                    {' '}
                    <OptionalTag />{' '}
                  </InputAdornment>
                ),
              }}
              helperText={
                !!errors.email && (errors.email.message || errors.email.type)
              }
              error={!!errors.email}
              InputLabelProps={{ shrink: shrink() }}
            />
            <Grid className="margin-top-basic" container spacing={2}>
              <Grid item xs={6}>
                <Controller
                  rules={{
                    required: true,
                    validate: (x) => x && x != 'statedefault',
                  }}
                  name="state"
                  disabled={!stateEditable}
                  defaultValue="statedefault"
                  as={
                    <Select error={!!errors.state} fullWidth variant="outlined">
                      <MenuItem disabled value={'statedefault'}>
                        <div className="placeholder-text">
                          {LocalizationService.STRINGS.state}
                        </div>
                      </MenuItem>
                      {[
                        ...states,
                        ...FirebaseService.getArray(
                          FirebaseConfigKey.RESTRICTED_COUNTRIES
                        ),
                      ]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((item) => (
                          <MenuItem key={item.code} value={item.code}>
                            {item.name.replace('*', '')}
                          </MenuItem>
                        ))}
                    </Select>
                  }
                  control={control}
                />
                <TextField
                  name="state"
                  onChange={setStateFromAutocomplete}
                  style={{ height: 0, width: 0 }}
                />
                {!!errors.state && (
                  <FormHelperText error>State is required</FormHelperText>
                )}
              </Grid>
              <Grid item xs>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="City"
                  name="city"
                  inputRef={register({ required: true })}
                  helperText={errors.city && 'City is required'}
                  error={!!errors.city}
                  InputLabelProps={{ shrink: shrink() }}
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              variant="outlined"
              className="margin-top-basic"
              label="Address"
              name="address"
              inputRef={register({ required: true })}
              helperText={
                errors.address && errors.address.type && 'Address is required'
              }
              error={!!errors.address}
              InputLabelProps={{ shrink: shrink() }}
            />
            <TextField
              fullWidth
              variant="outlined"
              className="margin-top-basic"
              label={LocalizationService.STRINGS.zipcode}
              name="zip"
              inputRef={register({
                required: true,
                validate: (x) => zipcodeRegex.test(x),
              })}
              helperText={
                !!errors.zip && `Invalid ${LocalizationService.STRINGS.zipcode}`
              }
              error={!!errors.zip}
              InputLabelProps={{ shrink: shrink() }}
            />
          </FlexView>
          <Divider />
        </React.Fragment>
      )}
      <FlexView
        hAlignContent={!isEditMode() && !showForm ? undefined : 'center'}
        className="space-between full-width">
        {!isEditMode() && !showForm && (
          <FlexView vAlignContent={'center'}>
            <AddSvg onClick={addAnother} className="clickable blue-svg-path" />
            <Button
              onClick={addAnother}
              variant="text"
              color="primary"
              style={{ textTransform: 'none' }}>
              Add Another
            </Button>
          </FlexView>
        )}
        <NewFillButton onClick={onSubmit}>
          {showForm ? 'Save' : 'Finish'}
        </NewFillButton>
      </FlexView>
    </React.Fragment>
  )
}
